import { ChangeDetectionStrategy, Component, computed, input } from "@angular/core";
import { CommonModule } from "@angular/common";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { S25ItemI } from "../../../pojo/S25ItemI";
import { Item } from "../../../pojo/Item";
import { SeOrganizationTileComponent } from "../organization/se.organization.tile.component";
import { SeEventTileComponent } from "../organization/se.event.tile.component";
import { StudentEngagementOrg } from "../StudentEngagementOrg";
import { S25States } from "../../../services/state/state.map";
import Name = S25States.Name;
import { SeEvent } from "../event/se.event.details.view.component";

/**
 * Unified interface for items that can be displayed in tiles
 */
export interface TileItem extends S25ItemI {
    itemTypeId: Item.Ids;
    // Event-specific properties (optional)
    date?: string;
    location?: string;
    description?: string;
    imageUrl?: string;
}

/**
 * Item Tile Component
 * Displays either an organization tile or event tile based on the item type
 */
@TypeManagerDecorator("se-item-tile")
@Component({
    selector: "se-item-tile",
    template: `
        @if (item()) {
            @if (isEvent()) {
                <se-event-tile [event]="asEvent()" [routingStateName]="routingStateName()"></se-event-tile>
            } @else if (isOrganization()) {
                <se-organization-tile
                    [org]="asOrganization()"
                    [routingStateName]="routingStateName()"
                ></se-organization-tile>
            }
        }
    `,
    styles: `
        :host {
            display: block;
            height: 100%;
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, SeOrganizationTileComponent, SeEventTileComponent],
})
export class ItemTileComponent {
    item = input.required<TileItem>();
    routingStateName = input<Name>();

    isEvent = computed(() => this.item().itemTypeId === Item.Ids.Event);
    isOrganization = computed(() => this.item().itemTypeId === Item.Ids.Organization);

    // Cast item as UpcomingEvent for event tile
    asEvent = computed((): SeEvent => {
        const item = this.item();
        if (!this.isEvent()) {
            return undefined;
        }
        return {
            itemId: item.itemId,
            itemName: item.itemName,
            itemTypeId: item.itemTypeId,
            date: item.date || "",
            location: item.location || "",
            description: item.description || "",
            imageUrl: item.imageUrl,
            txt: item.txt,
        };
    });

    // Cast item as S25ItemI for organization tile
    asOrganization = computed((): S25ItemI => {
        const item = this.item();
        if (!this.isOrganization()) {
            return undefined;
        }
        return {
            itemId: item.itemId,
            itemName: item.itemName,
            itemTypeId: item.itemTypeId,
            title: item.title,
            logoUrl: item.logoUrl,
            mission: item.mission,
        };
    });
}
